// ============================================================================
// @torre/expenses — Gastos y Presupuestos (CC-CONSOLE #615)
// ============================================================================
// Contrato compartido del control de gastos de la empresa: constantes,
// vocabularios, tipos y la lógica pura que el front (apps/v5) y el back
// (functions/src/modules/expenses, vía `build:functions`) usan igual:
//
//   · lectura del XML CFDI (#610)
//   · conciliación factura → gasto (#611)
//   · detección de cargos duplicados al importar (#609)
//   · vínculo comercio → plataforma y pagado mes a mes (#612)
//   · resumen mensual y pagos de nómina como gasto (#613)
//
// Sin dependencias, sin I/O. El cálculo de presupuesto contra gasto NO vive
// aquí: es `@torre/costos` (importado de AgroCloud, #607).
// ============================================================================

import { LOSS_REASONS, normalizeLoss } from "./losses";

export const TORRE_EXPENSES_VERSION = "0.1.0";

// --- Colecciones Firestore ----------------------------------------------------
export const EXPENSES_COLLECTION = "expenses";
export const EXPENSE_BUDGETS_COLLECTION = "expense_budgets";
export const EXPENSE_PLATFORMS_COLLECTION = "expense_platforms";
export const EXPENSE_INBOX_COLLECTION = "expense_inbox_messages";
export const EXPENSE_IMPORTS_COLLECTION = "expense_imports";
export const EXPENSE_SETTINGS_COLLECTION = "expense_settings";
/** Dinero que entra: depósitos de clientes y terceros leídos de los estados de cuenta. */
export const EXPENSE_INCOMES_COLLECTION = "expense_incomes";
/** Estados de cuenta subidos: el archivo va CIFRADO en Storage (#1029). */
export const EXPENSE_STATEMENTS_COLLECTION = "expense_statements";

/** Zona horaria con la que se asigna el mes de un gasto. */
export const EXPENSES_TIMEZONE = "America/Mexico_City";
/** Moneda base de los totales. */
export const BASE_CURRENCY = "MXN";

/**
 * Personas con acceso al módulo aunque nadie haya editado la lista todavía.
 * El backend suma a estas las de `EXPENSES_ADMIN_EMAILS` (env) y las guardadas
 * desde la sección Acceso. Se comparan en minúsculas. Solo van aquí correos
 * confirmados: un correo adivinado le daría acceso a quien lo registre.
 */
export const DEFAULT_EXPENSE_ADMIN_EMAILS: readonly string[] = [
  "isaias.cabrera@codingcloud.online",
  "icabrera@codingcloud.online",
  "daniel.guzman@codingcloud.online",
  "dguzman@codingcloud.online",
];

// --- Vocabularios -------------------------------------------------------------
export const EXPENSE_CATEGORIES = [
  "plataformas",
  // Lo que corre el producto en producción (nube, hosting, dominios, APIs por
  // consumo). Se separa de `plataformas` (herramientas del equipo): es costo
  // del servicio, no gasto de operación. Ver platformKind.ts.
  "infraestructura",
  "nomina",
  "compras",
  "servicios",
  "impuestos",
  "oficina",
  "viajes",
  "marketing",
  "hardware",
  "comisiones",
  "otros",
] as const;
/** Las categorías que trae el sistema (`EXPENSE_CATEGORIES`). */
export type BaseExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
/**
 * Categoría de un gasto, presupuesto o plataforma. Además de las del sistema,
 * cada compañía puede crear las suyas en Configuración de Finanzas (#1255), así
 * que el id es un texto: las del sistema conservan el autocompletado.
 */
export type ExpenseCategory = BaseExpenseCategory | (string & {});

/** Forma de un id de categoría propia (las del sistema también la cumplen). */
export const CATEGORY_ID_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;

export const EXPENSE_CATEGORY_LABELS: Record<BaseExpenseCategory, { es: string; en: string }> = {
  plataformas: { es: "Plataformas y herramientas", en: "Platforms & tools" },
  infraestructura: { es: "Infraestructura (nube, hosting y APIs)", en: "Infrastructure (cloud, hosting & APIs)" },
  nomina: { es: "Nómina y pagos a trabajadores", en: "Payroll & worker payments" },
  compras: { es: "Compras", en: "Purchases" },
  servicios: { es: "Servicios profesionales", en: "Professional services" },
  impuestos: { es: "Impuestos y derechos", en: "Taxes & fees" },
  oficina: { es: "Oficina", en: "Office" },
  viajes: { es: "Viajes y viáticos", en: "Travel" },
  marketing: { es: "Marketing y publicidad", en: "Marketing & ads" },
  hardware: { es: "Equipo y hardware", en: "Hardware" },
  comisiones: { es: "Comisiones bancarias", en: "Bank fees" },
  otros: { es: "Otros", en: "Other" },
};

// Tipo de costo: fijo (se paga cada mes pase lo que pase) o variable.
export const EXPENSE_COST_TYPES = ["fijo", "variable"] as const;
export type ExpenseCostType = (typeof EXPENSE_COST_TYPES)[number];

export const EXPENSE_COST_TYPE_LABELS: Record<ExpenseCostType, { es: string; en: string }> = {
  fijo: { es: "Gasto fijo", en: "Fixed cost" },
  variable: { es: "Gasto variable", en: "Variable cost" },
};

/** Categorías del sistema que son gasto fijo mientras el gasto no diga otra cosa. */
export const DEFAULT_FIXED_CATEGORIES: readonly BaseExpenseCategory[] = ["nomina", "plataformas"];

/** Etiqueta guardada de un gasto: `fijo`, `variable` o `null` (según su categoría). */
export function normalizeCostType(value: unknown): ExpenseCostType | null {
  return EXPENSE_COST_TYPES.includes(value as ExpenseCostType) ? (value as ExpenseCostType) : null;
}

// --- Catálogo de categorías de la compañía (#1255) ----------------------------
/** Lo mínimo que el resto del paquete necesita saber de una categoría. */
export interface ActiveCategory {
  id: string;
  /** Tipo de costo de sus gastos cuando el gasto no trae etiqueta. */
  costType: ExpenseCostType;
  archived?: boolean;
  label?: { es: string; en: string };
  color?: string;
}

/**
 * Catálogo ACTIVO: lo fija el front al cargar el de la compañía, para que las
 * gráficas, etiquetas y validaciones de pantalla lo usen sin pasarlo de mano en
 * mano. El back NO lo usa (atiende varias compañías a la vez): pasa la lista de
 * su compañía como parámetro (`categories`, `fixedCategories`). Sin catálogo
 * activo valen las categorías del sistema.
 */
let activeCategories: readonly ActiveCategory[] | null = null;
export function setActiveCategories(list: readonly ActiveCategory[] | null): void {
  activeCategories = list && list.length ? list : null;
}
export function getActiveCategories(): readonly ActiveCategory[] | null {
  return activeCategories;
}

/** Ids válidos: los de `allowed`, o el catálogo activo, o las del sistema. */
export function categoryIds(allowed?: readonly string[] | null): readonly string[] {
  if (allowed) return allowed;
  return activeCategories ? activeCategories.map((c) => c.id) : EXPENSE_CATEGORIES;
}
export function isCategoryId(value: unknown, allowed?: readonly string[] | null): boolean {
  return typeof value === "string" && categoryIds(allowed).includes(value);
}

/** ¿Los gastos de esta categoría son fijos por omisión? */
export function isFixedCategory(category: string, fixedCategories?: readonly string[] | null): boolean {
  if (fixedCategories) return fixedCategories.includes(category);
  const active = activeCategories?.find((c) => c.id === category);
  return active ? active.costType === "fijo" : (DEFAULT_FIXED_CATEGORIES as readonly string[]).includes(category);
}

type CostTypeInput = { category: ExpenseCategory; costType?: ExpenseCostType | null };

/** Tipo de costo efectivo: la etiqueta del gasto o, sin ella, la de su categoría. */
export function expenseCostType(e: CostTypeInput, fixedCategories?: readonly string[] | null): ExpenseCostType {
  return normalizeCostType(e.costType) ?? (isFixedCategory(e.category, fixedCategories) ? "fijo" : "variable");
}

/** `costType=fijo|variable` contra el tipo EFECTIVO; sin valor, todos. */
export function matchesCostType(e: CostTypeInput, value: unknown, fixedCategories?: readonly string[] | null): boolean {
  const wanted = normalizeCostType(value);
  return wanted ? expenseCostType(e, fixedCategories) === wanted : true;
}

export const PAYMENT_METHODS = [
  "tarjeta_credito",
  "tarjeta_debito",
  "transferencia",
  "domiciliacion",
  "efectivo",
  "otro",
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Estado de la factura de un gasto. `factura_extranjera`: recibo de un
 * proveedor del extranjero sin CFDI (Anthropic, GitHub…); cuenta como
 * comprobante y ya no está pendiente (#957).
 */
export const INVOICE_STATUSES = ["pendiente", "adjunta", "no_aplica", "factura_extranjera"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * Cómo nació el gasto. `nomina`/`equipo`/`padron` son de solo lectura (vienen de
 * RR. HH.); `padron` es la nómina ESTIMADA desde el sueldo del padrón.
 * `activos`: la compra de una factura de Activos (CC-CONSOLE #1638, PR 4). Nace
 * `por_revisar`, con `sourceRef { type: "asset_purchase", id }`.
 */
export const EXPENSE_SOURCES = ["manual", "ia_comprobante", "ia_estado_cuenta", "correo", "nomina", "equipo", "padron", "estado_cuenta", "activos"] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];

/** `por_revisar`: lo creó una automatización y una persona debe confirmarlo. */
export const EXPENSE_STATUSES = ["confirmado", "por_revisar"] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];

export const ATTACHMENT_KINDS = ["comprobante", "factura_pdf", "factura_xml"] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

export const PLATFORM_FREQUENCIES = ["mensual", "anual", "trimestral", "semanal", "uso"] as const;
export type PlatformFrequency = (typeof PLATFORM_FREQUENCIES)[number];

/** `por_confirmar`: factura de plataforma que espera confirmación en Pago de plataformas (#957). */
export const INBOX_RESULTS = ["adjuntada", "creada", "duplicada", "sin_factura", "error", "por_confirmar"] as const;
export type InboxResult = (typeof INBOX_RESULTS)[number];

/** Tipos de archivo que la IA sabe leer. */
export const EXTRACTABLE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"] as const;
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

// --- Tipos --------------------------------------------------------------------
export interface ExpenseAttachment {
  kind: AttachmentKind;
  /** Ruta en Storage; la URL se firma al leer. */
  path: string;
  name: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  url?: string | null;
  /** El archivo lo comparten varios gastos (una importación): no se borra con uno. */
  shared?: boolean;
  /** Estado de cuenta cifrado (#1029): no se firma URL; se abre con código al correo del dueño. */
  encrypted?: boolean;
  /** Documento de `expense_statements` que guarda el archivo cifrado. */
  statementId?: string | null;
  /** Factura de una compra de Activos (#1638): el archivo vive en Activos (`shared`). */
  fromAsset?: boolean;
}

/** Datos fiscales leídos de un CFDI. `null` = no venía en el comprobante. */
export interface InvoiceData {
  uuid: string | null;
  version: string | null;
  serie: string | null;
  folio: string | null;
  fecha: string | null;
  subtotal: number | null;
  total: number | null;
  moneda: string | null;
  tipoCambio: number | null;
  emisorRfc: string | null;
  emisorNombre: string | null;
  receptorRfc: string | null;
  receptorNombre: string | null;
  metodoPago: string | null;
  formaPago: string | null;
  conceptos: string[];
}

export interface Expense {
  id: string;
  company_id?: string;
  /** YYYY-MM-DD */
  date: string;
  /** YYYY-MM, derivado de `date`. */
  period: string;
  amount: number;
  currency: string;
  /**
   * Tipo de cambio a la moneda base (1 si ya es MXN). `null` = desconocido:
   * un cargo en dólares sin tipo de cambio NO vale lo mismo en pesos.
   */
  exchangeRate: number | null;
  /** `amount × exchangeRate`, en moneda base. `null` mientras falte el tipo de cambio. */
  amountBase: number | null;
  vendor: string;
  category: ExpenseCategory;
  /** Para qué sirve el gasto. */
  description: string;
  /** Por qué se gastó. */
  justification?: string | null;
  paymentMethod: PaymentMethod;
  cardLast4?: string | null;
  platformId?: string | null;
  /**
   * Cliente al que se atribuye el gasto (`work_hours_clients`, #980). `null` =
   * gasto propio de la compañía. Con él la app del cliente ve SOLO sus gastos.
   */
  clientId?: string | null;
  /**
   * Gasto que salió de una cuenta o tarjeta mezclada y NO es atribuible a la
   * empresa (#flujo). Se guarda para ver a dónde se va el dinero, pero no entra
   * al resumen, a los presupuestos ni a las plataformas de la empresa.
   */
  personal?: boolean;
  /** Por qué se atribuye a la empresa: `tdc_0065`, `nvio`, `plataforma`… `null` si no se sabe. */
  attributionReason?: string | null;
  /** Cuenta destino cuando el gasto es una dispersión de nómina (CLABE de NVIO…). */
  payoutAccount?: string | null;
  /**
   * Gasto pagado con dinero de una persona (tarjeta o cuenta propia): `true` =
   * REEMBOLSABLE (la empresa se lo debe: plataformas, nómina, servicios del
   * negocio); `false` = NO REEMBOLSABLE (es de la persona). `null` = no aplica
   * o sin decidir. Marcarlo fija también `personal` (no reembolsable = personal).
   */
  reimbursable?: boolean | null;
  /** Por qué es (o no) reembolsable. */
  reimbursableNote?: string | null;
  /**
   * Gasto fijo o variable. `null` = el de su categoría (`expenseCostType`): la
   * etiqueta solo se guarda cuando alguien decide distinto a la categoría.
   */
  costType?: ExpenseCostType | null;
  /**
   * Pérdida (#1177): dinero que salió y no dejó nada — suscripción sin uso,
   * cobro indebido, multa… El motivo es uno de `LOSS_REASONS` (./losses).
   */
  loss?: boolean | null;
  lossReason?: string | null;
  lossNote?: string | null;
  /** Cuenta bancaria o tarjeta de la que salió (#952, `expense_accounts`). */
  accountId?: string | null;
  /** Devolución (#954): monto negativo y el gasto que revierte. */
  kind?: "devolucion" | null;
  revertsExpenseId?: string | null;
  invoiceStatus: InvoiceStatus;
  invoice?: InvoiceData | null;
  attachments: ExpenseAttachment[];
  source: ExpenseSource;
  status: ExpenseStatus;
  /** Referencia al documento de origen (import, correo, recibo de nómina). */
  sourceRef?: { type: string; id: string } | null;
  /**
   * Compra de Activos ligada a este gasto (#1638). La pone el servidor al crear
   * o ligar; `null` cuando la compra se desligó (el gasto se queda).
   */
  assetPurchaseId?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
}

export type ExpenseInput = Partial<Omit<Expense, "id" | "period" | "amountBase">>;

export interface Platform {
  id: string;
  name: string;
  vendor?: string | null;
  /** Textos con los que aparece en el estado de cuenta ("GOOGLE *CLOUD", "ANTHROPIC"). */
  aliases: string[];
  category: ExpenseCategory;
  frequency: PlatformFrequency;
  expectedAmount?: number | null;
  currency: string;
  paymentMethod?: PaymentMethod | null;
  cardLast4?: string | null;
  /** Cómo se paga, en texto libre: tarjeta de quién, cargo automático, a nombre de quién sale la factura… */
  paymentNotes?: string | null;
  owner?: string | null;
  /** Día del mes en que suele cobrar. */
  billingDay?: number | null;
  active: boolean;
  url?: string | null;
  notes?: string | null;
  /** ISO de alta: antes de ese mes no se espera ningún cargo. */
  createdAt?: string;
  // --- Pago de plataformas (#956). Todos opcionales: una plataforma sin ellos
  // se lee como `servicio` con `expectedAmount` como tarifa (ver platformBilling.ts).
  /** Modelo de cobro: `asiento` | `servicio` | `consumo`. */
  billingModel?: "asiento" | "servicio" | "consumo" | null;
  /** Asientos contratados (modelo `asiento`). */
  seats?: number | null;
  /** Precio por asiento por periodo de cobro (modelo `asiento`). */
  seatPrice?: number | null;
  /** Quién ocupa cada asiento (opcional). */
  seatHolders?: { name: string; email?: string | null }[] | null;
  /** Unidad de consumo: tokens, GB, minutos… (modelo `consumo`). */
  consumptionUnit?: string | null;
  /** YYYY-MM-DD de la próxima renovación (anuales y trimestrales). */
  nextRenewal?: string | null;
  /** Alza tolerada del consumo contra el promedio de 3 meses (0.25 = 25 %). */
  spikeThreshold?: number | null;
  // --- Due diligence (ver platformDiligence.ts): quién la usa y cómo se factura.
  /** Repos y apps que la usan. */
  usedBy?: { repo: string; apps: string[]; purpose?: string | null }[] | null;
  /** Tenants / clientes a los que da servicio. */
  tenants?: string[] | null;
  /** Una misma cuenta compartida entre repos o tenants. */
  shared?: boolean | null;
  /** Licenciamiento: `uso` | `asiento` | `ambos` | `fijo` | `gratis`. */
  licensing?: "uso" | "asiento" | "ambos" | "fijo" | "gratis" | null;
  /** Comprobante esperado: `cfdi` | `factura_extranjera` | `recibo` | `sin_factura`. */
  invoiceType?: "cfdi" | "factura_extranjera" | "recibo" | "sin_factura" | null;
  /** Quién paga la cuenta. */
  paidBy?: string | null;
  /** YYYY-MM-DD de la última revisión. */
  reviewedAt?: string | null;
}

export interface BudgetLine {
  id: string;
  /** "2026" (anual) o "2026-09" (mensual). */
  period: string;
  category: ExpenseCategory;
  amount: number;
  notes?: string | null;
  /**
   * El ajuste semanal (#1178) puede cambiar esta línea al ritmo real. `false` =
   * fijada a mano: el ajuste no la toca. Sin valor cuenta como `true`.
   */
  autoAdjust?: boolean | null;
  /** Última vez que el ajuste automático cambió el monto (ISO). */
  adjustedAt?: string | null;
}

/** Un cargo leído por la IA, antes de convertirse en gasto. */
export interface ExtractedCharge {
  date: string | null;
  vendor: string;
  amount: number;
  currency: string;
  description: string;
  category: ExpenseCategory;
  cardLast4: string | null;
  /** Cargo que en realidad es un abono/pago a la tarjeta: no es gasto. */
  isCredit: boolean;
  confidence: "alta" | "media" | "baja";
  /**
   * Lo que el banco cobró en pesos por un cargo en otra moneda (el estado de
   * cuenta lo trae junto al importe original). `null` si no aplica o no viene.
   */
  billedAmount: number | null;
  /** Tipo de cambio a pesos: el del documento o `billedAmount / amount`. */
  exchangeRate: number | null;
}

export interface ExtractionResult {
  documentType: "estado_de_cuenta" | "recibo" | "captura" | "factura" | "otro";
  issuer: string | null;
  statementPeriod: { from: string | null; to: string | null } | null;
  statementTotal: number | null;
  currency: string | null;
  charges: ExtractedCharge[];
  notes: string | null;
}

export interface InboxMessage {
  id: string;
  messageId: string;
  from: string | null;
  subject: string | null;
  receivedAt: string | null;
  result: InboxResult;
  expenseId?: string | null;
  detail?: string | null;
  attachments: { name: string; contentType: string; path?: string | null }[];
  processedAt: string;
}

// --- Utilidades ---------------------------------------------------------------
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/**
 * Día (YYYY-MM-DD) de una fecha guardada de cualquier forma: texto ISO, `Date`
 * o `Timestamp` de Firestore (`toDate()` o `{ seconds | _seconds }`). `null` si
 * no se reconoce. Los colaboradores dados de alta desde Equipo guardan
 * `createdAt` como Timestamp: `.slice` sobre eso tumbaba la página.
 */
export function isoDayOf(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") return isIsoDate(value.slice(0, 10)) ? value.slice(0, 10) : null;
  let d: Date | null = null;
  if (value instanceof Date) d = value;
  else if (typeof value === "object") {
    const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof v.toDate === "function") d = v.toDate();
    else if (Number.isFinite(v.seconds)) d = new Date((v.seconds as number) * 1000);
    else if (Number.isFinite(v._seconds)) d = new Date((v._seconds as number) * 1000);
  }
  return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : null;
}

/** Etiqueta de un estado de cuenta: «Emisor · desde a hasta». Regla única para el API y el script de cifrado. */
export function statementLabelFrom(issuer: unknown, period: unknown, fallback = "Estado de cuenta"): string {
  const p = period as { from?: string | null; to?: string | null } | string | null | undefined;
  const range = typeof p === "string" ? p : p ? [p.from, p.to].filter(Boolean).join(" a ") : "";
  return [issuer ? String(issuer) : "", range].filter(Boolean).join(" · ") || fallback;
}

export function periodOf(date: string): string {
  return date.slice(0, 7);
}

/** Un periodo anual incluye sus meses: "2026" ⊇ "2026-07". */
export function periodIncludes(filter: string, period: string): boolean {
  return filter.length === 4 ? period.startsWith(`${filter}-`) || period === filter : period === filter;
}

export function daysBetween(a: string, b: string): number {
  const ms = Math.abs(new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime());
  return Math.round(ms / 86_400_000);
}

/** Minúsculas, sin acentos ni signos, espacios colapsados. */
export function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const VENDOR_NOISE = new Set([
  "sa", "de", "cv", "s", "a", "c", "v", "inc", "llc", "ltd", "sapi", "srl", "rl", "the", "com", "www", "mx",
  "paypal", "pp", "mercadopago", "stripe", "sq", "cobro", "pago", "compra", "cargo", "internet",
]);

/** Palabras significativas del nombre de un comercio. */
export function vendorTokens(value: unknown): string[] {
  return normalizeText(value)
    .split(" ")
    .filter((t) => t.length > 1 && !VENDOR_NOISE.has(t) && !/^\d+$/.test(t));
}

/** Similitud 0..1 entre dos nombres de comercio (tokens compartidos). */
export function vendorSimilarity(a: unknown, b: unknown): number {
  const ta = vendorTokens(a);
  const tb = vendorTokens(b);
  if (!ta.length || !tb.length) return 0;
  const sb = new Set(tb);
  let shared = 0;
  for (const t of ta) {
    if (sb.has(t) || tb.some((x) => x.length >= 4 && t.length >= 4 && (x.startsWith(t) || t.startsWith(x)))) shared += 1;
  }
  return shared / Math.min(ta.length, tb.length);
}

// --- Validación ---------------------------------------------------------------
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/** `categories`: ids válidos de la compañía; sin ella valen el catálogo activo o las del sistema. */
type CategoryRule = { categories?: readonly string[] | null };

export function validateExpense(input: ExpenseInput, { partial = false, categories }: { partial?: boolean } & CategoryRule = {}): ValidationResult {
  const errors: string[] = [];
  const has = (k: keyof ExpenseInput) => input[k] !== undefined;
  if (!partial || has("date")) if (!isIsoDate(input.date)) errors.push("date: debe ser YYYY-MM-DD");
  if (!partial || has("amount")) {
    if (typeof input.amount !== "number" || !Number.isFinite(input.amount) || input.amount < 0) {
      errors.push("amount: debe ser un número mayor o igual a 0");
    }
  }
  if (!partial || has("vendor")) if (!String(input.vendor ?? "").trim()) errors.push("vendor: requerido");
  if (has("category") && !isCategoryId(input.category, categories)) errors.push("category: inválida");
  if (has("paymentMethod") && !PAYMENT_METHODS.includes(input.paymentMethod as PaymentMethod)) errors.push("paymentMethod: inválido");
  if (has("invoiceStatus") && !INVOICE_STATUSES.includes(input.invoiceStatus as InvoiceStatus)) errors.push("invoiceStatus: inválido");
  if (has("status") && !EXPENSE_STATUSES.includes(input.status as ExpenseStatus)) errors.push("status: inválido");
  if (input.costType != null && !EXPENSE_COST_TYPES.includes(input.costType as ExpenseCostType)) errors.push("costType: fijo o variable");
  if (input.loss === true && input.lossReason != null && !(LOSS_REASONS as readonly string[]).includes(String(input.lossReason))) errors.push("lossReason: motivo de pérdida inválido");
  if (has("currency") && !/^[A-Z]{3}$/.test(String(input.currency))) errors.push("currency: código ISO de 3 letras");
  if (has("exchangeRate")) {
    const r = input.exchangeRate;
    if (r !== null && (typeof r !== "number" || !Number.isFinite(r) || r <= 0)) errors.push("exchangeRate: debe ser mayor que 0");
  }
  if (input.cardLast4 != null && input.cardLast4 !== "" && !/^\d{4}$/.test(String(input.cardLast4))) {
    errors.push("cardLast4: 4 dígitos");
  }
  return { valid: errors.length === 0, errors };
}

export function validateBudgetLine(input: Partial<BudgetLine>, { categories }: CategoryRule = {}): ValidationResult {
  const errors: string[] = [];
  if (!/^\d{4}(-(0[1-9]|1[0-2]))?$/.test(String(input.period ?? ""))) errors.push("period: YYYY o YYYY-MM");
  if (!isCategoryId(input.category, categories)) errors.push("category: inválida");
  if (typeof input.amount !== "number" || !Number.isFinite(input.amount) || input.amount < 0) errors.push("amount: número ≥ 0");
  if (input.autoAdjust != null && typeof input.autoAdjust !== "boolean") errors.push("autoAdjust: true o false");
  return { valid: errors.length === 0, errors };
}

export function validatePlatform(input: Partial<Platform>, { categories }: CategoryRule = {}): ValidationResult {
  const errors: string[] = [];
  if (!String(input.name ?? "").trim()) errors.push("name: requerido");
  if (input.category !== undefined && !isCategoryId(input.category, categories)) errors.push("category: inválida");
  if (input.frequency !== undefined && !PLATFORM_FREQUENCIES.includes(input.frequency)) errors.push("frequency: inválida");
  if (input.expectedAmount != null && (!Number.isFinite(input.expectedAmount) || input.expectedAmount < 0)) {
    errors.push("expectedAmount: número ≥ 0");
  }
  if (input.billingDay != null && (!Number.isInteger(input.billingDay) || input.billingDay < 1 || input.billingDay > 31)) {
    errors.push("billingDay: 1 a 31");
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Normaliza la entrada de un gasto a su forma guardada. Completa los campos
 * derivados (`period`, `amountBase`) y los valores por defecto.
 */
export function normalizeExpense(input: ExpenseInput, base?: Partial<Expense>): Omit<Expense, "id"> {
  const merged = { ...(base || {}), ...stripUndefined(input) } as Partial<Expense>;
  const currency = String(merged.currency || BASE_CURRENCY).toUpperCase();
  const exchangeRate = currency === BASE_CURRENCY ? 1 : Number(merged.exchangeRate) > 0 ? Number(merged.exchangeRate) : null;
  const amount = round2(Number(merged.amount) || 0);
  const date = String(merged.date);
  const attachments = Array.isArray(merged.attachments) ? merged.attachments : [];
  const hasInvoiceFile = attachments.some((a) => a.kind === "factura_pdf" || a.kind === "factura_xml");
  const reimbursable = typeof merged.reimbursable === "boolean" ? merged.reimbursable : null;
  let invoiceStatus: InvoiceStatus = (merged.invoiceStatus as InvoiceStatus) || "pendiente";
  if (hasInvoiceFile && invoiceStatus === "pendiente") invoiceStatus = "adjunta";
  if (!hasInvoiceFile && invoiceStatus === "adjunta") invoiceStatus = "pendiente";
  return {
    ...(merged as Expense),
    date,
    period: periodOf(date),
    amount,
    currency,
    exchangeRate,
    amountBase: exchangeRate == null ? null : round2(amount * exchangeRate),
    vendor: String(merged.vendor || "").trim(),
    category: (merged.category as ExpenseCategory) || "otros",
    description: String(merged.description || "").trim(),
    justification: merged.justification ? String(merged.justification).trim() : null,
    paymentMethod: (merged.paymentMethod as PaymentMethod) || "tarjeta_credito",
    cardLast4: merged.cardLast4 ? String(merged.cardLast4) : null,
    platformId: merged.platformId || null,
    clientId: merged.clientId || null,
    personal: reimbursable === false ? true : reimbursable === true ? false : merged.personal === true,
    attributionReason: merged.attributionReason ? String(merged.attributionReason) : null,
    reimbursable,
    reimbursableNote: merged.reimbursableNote ? String(merged.reimbursableNote).trim() : null,
    costType: normalizeCostType(merged.costType),
    ...normalizeLoss(merged),
    invoiceStatus,
    invoice: merged.invoice || null,
    attachments,
    source: (merged.source as ExpenseSource) || "manual",
    status: (merged.status as ExpenseStatus) || "confirmado",
    sourceRef: merged.sourceRef || null,
    notes: merged.notes ? String(merged.notes) : null,
  };
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

// --- CFDI (#610) --------------------------------------------------------------
function attr(tag: string, name: string): string | null {
  const m = new RegExp(`\\s${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i").exec(tag);
  if (!m) return null;
  return decodeXml(m[2] ?? m[3] ?? "");
}

function firstTag(xml: string, local: string): string | null {
  const m = new RegExp(`<(?:[A-Za-z0-9_]+:)?${local}\\b[^>]*>`, "i").exec(xml);
  return m ? m[0] : null;
}

function allTags(xml: string, local: string): string[] {
  return xml.match(new RegExp(`<(?:[A-Za-z0-9_]+:)?${local}\\b[^>]*>`, "gi")) || [];
}

function decodeXml(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&amp;/g, "&");
}

const num = (v: string | null): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function isCfdiXml(xml: string): boolean {
  return /<(?:[A-Za-z0-9_]+:)?Comprobante\b/i.test(xml) && /sat\.gob\.mx\/cfd/i.test(xml);
}

/**
 * Lee los datos fiscales de un CFDI 3.3/4.0. Devuelve `null` si el XML no es
 * un CFDI. Un campo ausente queda en `null`, nunca en 0.
 */
export function parseCfdiXml(xml: string): InvoiceData | null {
  if (typeof xml !== "string" || !isCfdiXml(xml)) return null;
  const comp = firstTag(xml, "Comprobante") as string;
  const emisor = firstTag(xml, "Emisor");
  const receptor = firstTag(xml, "Receptor");
  const timbre = firstTag(xml, "TimbreFiscalDigital");
  const conceptos = allTags(xml, "Concepto")
    .map((t) => attr(t, "Descripcion"))
    .filter((d): d is string => Boolean(d));
  const uuid = timbre ? attr(timbre, "UUID") : null;
  return {
    uuid: uuid ? uuid.toUpperCase() : null,
    version: attr(comp, "Version"),
    serie: attr(comp, "Serie"),
    folio: attr(comp, "Folio"),
    fecha: attr(comp, "Fecha"),
    subtotal: num(attr(comp, "SubTotal")),
    total: num(attr(comp, "Total")),
    moneda: attr(comp, "Moneda"),
    tipoCambio: num(attr(comp, "TipoCambio")),
    emisorRfc: emisor ? attr(emisor, "Rfc") : null,
    emisorNombre: emisor ? attr(emisor, "Nombre") : null,
    receptorRfc: receptor ? attr(receptor, "Rfc") : null,
    receptorNombre: receptor ? attr(receptor, "Nombre") : null,
    metodoPago: attr(comp, "MetodoPago"),
    formaPago: attr(comp, "FormaPago"),
    conceptos,
  };
}

// --- Conciliación factura → gasto (#611) --------------------------------------
export interface InvoiceMatchOptions {
  /** Días de diferencia tolerados entre la factura y el cargo. */
  maxDays?: number;
  /** Diferencia relativa tolerada en el monto (0.01 = 1 %). */
  amountTolerance?: number;
}

export interface InvoiceMatch {
  expenseId: string;
  score: number;
  reasons: string[];
}

export interface InvoiceLike {
  uuid?: string | null;
  total: number | null;
  moneda?: string | null;
  /** YYYY-MM-DD o ISO completo. */
  fecha?: string | null;
  emisorNombre?: string | null;
  emisorRfc?: string | null;
}

/**
 * Busca el gasto al que corresponde una factura. Reglas:
 *  - Un gasto que ya tiene ESA factura (mismo UUID) gana siempre.
 *  - Un gasto con otra factura adjunta no se toca.
 *  - El monto debe coincidir (dentro de la tolerancia) en la moneda del gasto;
 *    sin monto en la factura no hay conciliación.
 *  - La fecha debe estar dentro de la ventana.
 *  - Entre varios candidatos gana el de comercio más parecido y fecha más cercana.
 *    Si dos empatan, no se elige ninguno: es mejor crear un gasto "por revisar"
 *    que colgar la factura al gasto equivocado.
 */
export function matchInvoiceToExpense(
  invoice: InvoiceLike,
  expenses: readonly Expense[],
  { maxDays = 7, amountTolerance = 0.01 }: InvoiceMatchOptions = {},
): InvoiceMatch | null {
  const uuid = invoice.uuid ? invoice.uuid.toUpperCase() : null;
  if (uuid) {
    const same = expenses.find((e) => e.invoice?.uuid && e.invoice.uuid.toUpperCase() === uuid);
    if (same) return { expenseId: same.id, score: 1, reasons: ["mismo folio fiscal"] };
  }
  const total = invoice.total;
  if (total == null || !Number.isFinite(total) || total <= 0) return null;
  const date = invoice.fecha ? invoice.fecha.slice(0, 10) : null;
  const currency = (invoice.moneda || BASE_CURRENCY).toUpperCase().replace(/^XXX$/, BASE_CURRENCY);

  const candidates: InvoiceMatch[] = [];
  for (const e of expenses) {
    if (isPayrollSource(e.source)) continue;
    if (e.invoice?.uuid) continue;
    if (e.attachments?.some((a) => a.kind === "factura_xml")) continue;
    const reasons: string[] = [];
    const sameCurrency = (e.currency || BASE_CURRENCY) === currency;
    const compare = sameCurrency ? e.amount : e.amountBase;
    const target = sameCurrency ? total : currency === BASE_CURRENCY ? total : null;
    if (target == null || compare == null) continue;
    const diff = Math.abs(compare - target) / Math.max(target, 1);
    if (diff > amountTolerance) continue;
    reasons.push(diff === 0 ? "mismo monto" : "monto dentro de tolerancia");
    let dayGap = 0;
    if (date && isIsoDate(date)) {
      dayGap = daysBetween(date, e.date);
      if (dayGap > maxDays) continue;
      reasons.push(`${dayGap} día(s) de diferencia`);
    }
    const sim = Math.max(vendorSimilarity(invoice.emisorNombre, e.vendor), invoice.emisorRfc && e.invoice?.emisorRfc === invoice.emisorRfc ? 1 : 0);
    if (sim > 0) reasons.push("comercio coincide");
    const score = round2(0.5 + 0.3 * sim + 0.2 * (1 - dayGap / (maxDays + 1)) - diff);
    candidates.push({ expenseId: e.id, score, reasons });
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.score - a.score);
  if (candidates.length > 1 && candidates[0].score === candidates[1].score) return null;
  return candidates[0];
}

// --- Duplicados al importar (#609) --------------------------------------------
/**
 * ¿Ya existe este cargo? Mismo monto, misma moneda, fecha a ±1 día y comercio
 * parecido (o sin comercio comparable). Devuelve el id del gasto existente.
 */
export function findDuplicateExpense(
  charge: Pick<ExtractedCharge, "date" | "amount" | "currency" | "vendor">,
  expenses: readonly Pick<Expense, "id" | "date" | "amount" | "currency" | "vendor">[],
): string | null {
  if (!charge.date) return null;
  const currency = (charge.currency || BASE_CURRENCY).toUpperCase();
  for (const e of expenses) {
    if ((e.currency || BASE_CURRENCY) !== currency) continue;
    if (Math.abs(round2(e.amount) - round2(charge.amount)) > 0.005) continue;
    if (daysBetween(e.date, charge.date) > 1) continue;
    const ta = vendorTokens(charge.vendor);
    const tb = vendorTokens(e.vendor);
    if (ta.length && tb.length && vendorSimilarity(charge.vendor, e.vendor) === 0) continue;
    return e.id;
  }
  return null;
}

/** Normaliza lo que devolvió la IA: descarta basura y abonos, completa defaults. */
export function sanitizeExtraction(raw: unknown, { categories }: CategoryRule = {}): ExtractionResult {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const types = ["estado_de_cuenta", "recibo", "captura", "factura", "otro"] as const;
  const charges: ExtractedCharge[] = [];
  for (const c of Array.isArray(r.charges) ? r.charges : []) {
    if (!c || typeof c !== "object") continue;
    const x = c as Record<string, unknown>;
    const amount = Number(x.amount);
    if (!Number.isFinite(amount) || amount === 0) continue;
    const date = isIsoDate(x.date) ? (x.date as string) : null;
    const category = isCategoryId(x.category, categories) ? (x.category as ExpenseCategory) : "otros";
    const last4 = /^\d{4}$/.test(String(x.cardLast4 ?? "")) ? String(x.cardLast4) : null;
    const confidence = ["alta", "media", "baja"].includes(String(x.confidence)) ? (x.confidence as ExtractedCharge["confidence"]) : "media";
    const currency = /^[A-Z]{3}$/.test(String(x.currency ?? "")) ? String(x.currency) : BASE_CURRENCY;
    const abs = round2(Math.abs(amount));
    // Solo cuenta como cobro en pesos si la moneda del cargo no es MXN.
    const billedRaw = Math.abs(Number(x.billedAmount));
    const billedAmount = currency !== BASE_CURRENCY && Number.isFinite(billedRaw) && billedRaw > 0 ? round2(billedRaw) : null;
    const rateRaw = Number(x.exchangeRate);
    let exchangeRate: number | null = null;
    if (currency === BASE_CURRENCY) exchangeRate = 1;
    else if (billedAmount != null) exchangeRate = Math.round((billedAmount / abs) * 10000) / 10000;
    else if (Number.isFinite(rateRaw) && rateRaw > 0) exchangeRate = rateRaw;
    charges.push({
      date,
      vendor: String(x.vendor ?? "").trim() || "Sin comercio",
      amount: abs,
      currency,
      description: String(x.description ?? "").trim(),
      category,
      cardLast4: last4,
      isCredit: Boolean(x.isCredit) || amount < 0,
      confidence,
      billedAmount,
      exchangeRate,
    });
  }
  const period = r.statementPeriod as Record<string, unknown> | null | undefined;
  const total = Number(r.statementTotal);
  return {
    documentType: types.includes(r.documentType as (typeof types)[number]) ? (r.documentType as ExtractionResult["documentType"]) : "otro",
    issuer: r.issuer ? String(r.issuer) : null,
    statementPeriod: period ? { from: isIsoDate(period.from) ? (period.from as string) : null, to: isIsoDate(period.to) ? (period.to as string) : null } : null,
    statementTotal: r.statementTotal == null || !Number.isFinite(total) ? null : round2(total),
    currency: /^[A-Z]{3}$/.test(String(r.currency ?? "")) ? String(r.currency) : null,
    charges,
    notes: r.notes ? String(r.notes) : null,
  };
}

/**
 * Suma de los cargos (sin abonos) por moneda: lo que "se pagó" en el
 * documento. Un cargo en dólares que el banco cobró en pesos suma en pesos,
 * que es como lo suma el propio estado de cuenta.
 */
export function extractionTotals(result: Pick<ExtractionResult, "charges">): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of result.charges) {
    if (c.isCredit) continue;
    const [cur, value] = c.billedAmount != null ? [BASE_CURRENCY, c.billedAmount] : [c.currency, c.amount];
    out[cur] = round2((out[cur] || 0) + value);
  }
  return out;
}

// --- Plataformas (#612) -------------------------------------------------------
/** Plataforma a la que pertenece un comercio del estado de cuenta, si la hay. */
export function matchPlatform(
  vendor: string,
  platforms: readonly Platform[],
  /**
   * `true`: solo nombre exacto o palabra completa. Descarta «contenido sin
   * espacios» y el parecido por prefijo («HEREDEROS» contiene «HERE»).
   */
  strict = false,
): string | null {
  const v = normalizeText(vendor);
  if (!v) return null;
  let best: { id: string; score: number } | null = null;
  // Otra plataforma con el mismo mejor puntaje: la coincidencia es ambigua.
  let tied = false;
  for (const p of platforms) {
    if (p.active === false) continue;
    const names = [p.name, p.vendor, ...(p.aliases || [])].filter(Boolean) as string[];
    for (const n of names) {
      const nn = normalizeText(n);
      if (!nn) continue;
      let score = 0;
      if (v === nn) score = 1;
      else if (nn.length >= 4 && ` ${v} `.includes(` ${nn} `)) score = 0.9;
      else if (nn.length >= 4 && v.replace(/ /g, "").includes(nn.replace(/ /g, ""))) score = 0.8;
      else score = vendorSimilarity(vendor, n) >= 1 ? 0.7 : 0;
      if (strict && score < 0.9) continue;
      if (score <= 0) continue;
      if (!best || score > best.score) {
        best = { id: p.id, score };
        tied = false;
      } else if (score === best.score && p.id !== best.id) tied = true;
    }
  }
  return best && !tied ? best.id : null;
}

export interface PlatformMonth {
  period: string;
  paid: number;
  charges: number;
  /** Cargos del mes sin tipo de cambio (no suman a `paid`). */
  withoutRate: number;
  /** Se esperaba un cargo este mes y no hubo. */
  missing: boolean;
}

export interface PlatformReport {
  platformId: string;
  name: string;
  expectedMonthly: number | null;
  totalPaid: number;
  months: PlatformMonth[];
}

/** Lista de periodos YYYY-MM entre dos meses, inclusive. */
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  let [y, m] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    if (out.length > 240) break;
  }
  return out;
}

export function expectedMonthlyAmount(p: Pick<Platform, "frequency" | "expectedAmount">): number | null {
  if (p.expectedAmount == null) return null;
  switch (p.frequency) {
    case "mensual":
      return p.expectedAmount;
    case "anual":
      return round2(p.expectedAmount / 12);
    case "trimestral":
      return round2(p.expectedAmount / 3);
    case "semanal":
      return round2((p.expectedAmount * 52) / 12);
    default:
      return null;
  }
}

/** Pagado por plataforma, mes a mes (en moneda base), y los meses sin cargo. */
export function platformMonthlyReport(
  platforms: readonly Platform[],
  expenses: readonly Expense[],
  from: string,
  to: string,
  /** Hoy (YYYY-MM-DD, hora de México): el mes en curso solo falta si ya pasó el día de cobro. */
  today?: string,
): PlatformReport[] {
  const months = monthRange(from, to);
  return platforms.map((p) => {
    const own = expenses.filter((e) => e.platformId === p.id);
    const rows = months.map((period) => {
      const inMonth = own.filter((e) => e.period === period);
      return {
        period,
        paid: round2(inMonth.reduce((s, e) => s + (e.amountBase ?? 0), 0)),
        withoutRate: inMonth.filter((e) => e.amountBase == null).length,
        charges: inMonth.length,
        missing: p.active !== false && p.frequency === "mensual" && inMonth.length === 0 && isDue(p, period, today),
      };
    });
    return {
      platformId: p.id,
      name: p.name,
      expectedMonthly: expectedMonthlyAmount(p),
      totalPaid: round2(rows.reduce((s, r) => s + r.paid, 0)),
      months: rows,
    };
  });
}

/** ¿Ya debía haber un cargo de la plataforma en ese mes? */
function isDue(p: Pick<Platform, "createdAt" | "billingDay">, period: string, today?: string): boolean {
  // Antes del mes de alta no se llevaba la plataforma: no es un faltante.
  if (p.createdAt && period < p.createdAt.slice(0, 7)) return false;
  if (today && period > today.slice(0, 7)) return false;
  if (today && period === today.slice(0, 7)) {
    const day = Number(today.slice(8, 10));
    return p.billingDay != null ? day > p.billingDay : false;
  }
  return true;
}

// --- Resumen mensual (#613) ---------------------------------------------------
export interface MonthSummary {
  period: string;
  total: number;
  count: number;
  withoutInvoice: number;
  toReview: number;
  /** Gastos en otra moneda sin tipo de cambio: no suman al total hasta tenerlo. */
  withoutRate: number;
  byCategory: Partial<Record<ExpenseCategory, number>>;
  byPaymentMethod: Record<string, number>;
  byCard: Record<string, number>;
}

export function monthlySummary(expenses: readonly Expense[], from?: string, to?: string): MonthSummary[] {
  const periods = new Set(expenses.map((e) => e.period));
  const list = from && to ? monthRange(from, to) : [...periods].sort();
  return list.map((period) => {
    const rows = expenses.filter((e) => e.period === period);
    const s: MonthSummary = { period, total: 0, count: rows.length, withoutInvoice: 0, toReview: 0, withoutRate: 0, byCategory: {}, byPaymentMethod: {}, byCard: {} };
    for (const e of rows) {
      if (e.invoiceStatus === "pendiente" && !isPayrollSource(e.source)) s.withoutInvoice += 1;
      if (e.status === "por_revisar") s.toReview += 1;
      if (e.amountBase == null) {
        s.withoutRate += 1;
        continue;
      }
      const v = e.amountBase;
      s.total += v;
      s.byCategory[e.category] = round2((s.byCategory[e.category] || 0) + v);
      s.byPaymentMethod[e.paymentMethod] = round2((s.byPaymentMethod[e.paymentMethod] || 0) + v);
      if (e.cardLast4) s.byCard[e.cardLast4] = round2((s.byCard[e.cardLast4] || 0) + v);
    }
    s.total = round2(s.total);
    return s;
  });
}

// --- Ingresos y flujo de efectivo (ingresos contra egresos) -----------------------
/**
 * `devolucion`: dinero que regresa de un cargo (resta al gasto, no es ingreso).
 * `rendimiento`: intereses que paga el banco sobre el saldo.
 */
export const INCOME_PAYER_TYPES = ["cliente", "persona", "otro", "devolucion", "rendimiento"] as const;
export type IncomePayerType = (typeof INCOME_PAYER_TYPES)[number];

/** Dinero que entró: un depósito de un cliente o tercero. */
export interface Income {
  id: string;
  company_id?: string;
  /** YYYY-MM-DD */
  date: string;
  /** YYYY-MM, derivado de `date`. */
  period: string;
  amount: number;
  currency: string;
  exchangeRate: number | null;
  amountBase: number | null;
  /** Quién pagó, como lo trae el banco ("COMERCIALIZADORA ALFATEC SA DE CV"). */
  payer: string;
  payerType: IncomePayerType;
  /** Concepto del depósito ("TRANSFERENCIA", "pago"). */
  concept?: string | null;
  /** Cuenta donde entró ("Cuenta Nu 00017435843"). */
  account?: string | null;
  /**
   * Cliente al que pertenece quien pagó: un mismo cliente paga con varias razones
   * sociales (Dumax: BETAMAR, ALFATEC, GAMALUX…). «Quién pagó» se agrupa por aquí.
   */
  client?: string | null;
  /** Banco de origen ("AFIRME", "STP"). */
  bank?: string | null;
  source: "manual" | "estado_cuenta";
  /** Dinero que entró a una cuenta personal y no es ingreso de la empresa (rendimientos, devoluciones de gastos personales). */
  personal?: boolean;
  sourceRef?: { type: string; id: string } | null;
  /**
   * Factura del CRM (`crm_invoices`) de este ingreso y su folio. Los pone el
   * backend: todo ingreso de un cliente tiene una; devoluciones, rendimientos y
   * dinero personal no.
   */
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
}

export type IncomeInput = Partial<Omit<Income, "id" | "period" | "amountBase">>;

export function validateIncome(input: IncomeInput, { partial = false } = {}): ValidationResult {
  const errors: string[] = [];
  const has = (k: keyof IncomeInput) => input[k] !== undefined;
  if (!partial || has("date")) if (!isIsoDate(input.date)) errors.push("date: debe ser YYYY-MM-DD");
  if (!partial || has("amount")) {
    if (typeof input.amount !== "number" || !Number.isFinite(input.amount) || input.amount <= 0) errors.push("amount: debe ser un número mayor que 0");
  }
  if (!partial || has("payer")) if (!String(input.payer ?? "").trim()) errors.push("payer: requerido");
  if (has("payerType") && !INCOME_PAYER_TYPES.includes(input.payerType as IncomePayerType)) errors.push("payerType: inválido");
  if (has("currency") && !/^[A-Z]{3}$/.test(String(input.currency))) errors.push("currency: código ISO de 3 letras");
  return { valid: errors.length === 0, errors };
}

export function normalizeIncome(input: IncomeInput, base?: Partial<Income>): Omit<Income, "id"> {
  const merged = { ...(base || {}), ...stripUndefined(input) } as Partial<Income>;
  const currency = String(merged.currency || BASE_CURRENCY).toUpperCase();
  const exchangeRate = currency === BASE_CURRENCY ? 1 : Number(merged.exchangeRate) > 0 ? Number(merged.exchangeRate) : null;
  const amount = round2(Number(merged.amount) || 0);
  const date = String(merged.date);
  return {
    ...(merged as Income),
    date,
    period: periodOf(date),
    amount,
    currency,
    exchangeRate,
    amountBase: exchangeRate == null ? null : round2(amount * exchangeRate),
    payer: String(merged.payer || "").trim(),
    payerType: (merged.payerType as IncomePayerType) || "cliente",
    concept: merged.concept ? String(merged.concept).trim() : null,
    account: merged.account ? String(merged.account).trim() : null,
    bank: merged.bank ? String(merged.bank).trim() : null,
    client: merged.client ? String(merged.client).trim() : null,
    source: merged.source === "estado_cuenta" ? "estado_cuenta" : "manual",
    personal: merged.personal === true,
    sourceRef: merged.sourceRef || null,
    notes: merged.notes ? String(merged.notes) : null,
  };
}

export interface CashflowMonth {
  period: string;
  income: number;
  /** Egresos de la empresa (sin los marcados como personales). */
  companySpend: number;
  /** Egresos no atribuibles a la empresa. */
  personalSpend: number;
  /** Ingresos − egresos de la empresa. */
  net: number;
  /** Entró a cuentas personales y no es de la empresa (rendimientos…). */
  personalIncome: number;
  /** Devoluciones de cargos (ya restadas del gasto de la empresa o del no atribuible). */
  refunds: number;
  incomeCount: number;
  expenseCount: number;
}

export interface CashflowSummary {
  months: CashflowMonth[];
  totals: { income: number; companySpend: number; personalSpend: number; net: number; personalIncome: number; refunds: number };
  /** Gastos pagados con dinero de una persona: cuánto es reembolsable, cuánto no y cuánto falta decidir. */
  reimbursable: { yes: number; no: number; pending: number; yesCount: number; noCount: number; pendingCount: number };
  /** Quién pagó, de mayor a menor. */
  /** Por cliente (o por quien pagó si no tiene cliente), con las razones sociales con que pagó. */
  payers: { payer: string; payerType: IncomePayerType; total: number; count: number; lastDate: string; entities: string[] }[];
  /** En qué se va el dinero, por categoría, separando empresa y no atribuible. */
  categories: { category: ExpenseCategory; company: number; personal: number }[];
  /** Por qué se atribuye a la empresa (`tdc_0065`, `nvio`, `plataforma`, `sin_regla`). */
  attribution: Record<string, number>;
  /** Proveedores con más gasto de la empresa. */
  topVendors: { vendor: string; total: number; count: number }[];
  /** Movimientos sin tipo de cambio: no suman hasta tenerlo. */
  withoutRate: number;
}

/**
 * Ingresos contra egresos por mes (#flujo). Todo en moneda base; lo que no tiene
 * tipo de cambio no se inventa: se cuenta en `withoutRate`.
 */
export function cashflowSummary(
  expenses: readonly Expense[],
  incomes: readonly Income[],
  from: string,
  to: string,
): CashflowSummary {
  const months = monthRange(from, to).map<CashflowMonth>((period) => ({ period, income: 0, companySpend: 0, personalSpend: 0, net: 0, personalIncome: 0, refunds: 0, incomeCount: 0, expenseCount: 0 }));
  const reimb = { yes: 0, no: 0, pending: 0, yesCount: 0, noCount: 0, pendingCount: 0 };
  const byPeriod = new Map(months.map((m) => [m.period, m]));
  const payers = new Map<string, { payer: string; payerType: IncomePayerType; total: number; count: number; lastDate: string; entities: string[] }>();
  const cats = new Map<ExpenseCategory, { category: ExpenseCategory; company: number; personal: number }>();
  const vendors = new Map<string, { vendor: string; total: number; count: number }>();
  const attribution: Record<string, number> = {};
  let withoutRate = 0;
  for (const i of incomes) {
    const m = byPeriod.get(i.period);
    if (!m) continue;
    if (i.amountBase == null) {
      withoutRate += 1;
      continue;
    }
    if (i.payerType === "devolucion") {
      // Una devolución no es ingreso: reduce lo gastado del lado que corresponde.
      if (i.personal) m.personalSpend -= i.amountBase;
      else m.companySpend -= i.amountBase;
      m.refunds += i.amountBase;
      continue;
    }
    if (i.personal) {
      m.personalIncome += i.amountBase;
      continue;
    }
    m.income += i.amountBase;
    m.incomeCount += 1;
    const name = i.client || i.payer;
    const key = normalizeText(name);
    const p = payers.get(key) || { payer: name, payerType: i.payerType, total: 0, count: 0, lastDate: i.date, entities: [] };
    if (i.payer && !p.entities.some((x) => normalizeText(x) === normalizeText(i.payer))) p.entities.push(i.payer);
    p.total += i.amountBase;
    p.count += 1;
    if (i.date > p.lastDate) p.lastDate = i.date;
    payers.set(key, p);
  }
  for (const e of expenses) {
    const m = byPeriod.get(e.period);
    if (!m) continue;
    if (e.amountBase == null) {
      withoutRate += 1;
      continue;
    }
    const v = e.amountBase;
    m.expenseCount += 1;
    if (e.reimbursable === true) {
      reimb.yes += v;
      reimb.yesCount += 1;
    } else if (e.reimbursable === false) {
      reimb.no += v;
      reimb.noCount += 1;
    } else if (e.source === "estado_cuenta") {
      reimb.pending += v;
      reimb.pendingCount += 1;
    }
    const c = cats.get(e.category) || { category: e.category, company: 0, personal: 0 };
    if (e.personal) {
      m.personalSpend += v;
      c.personal += v;
    } else {
      m.companySpend += v;
      c.company += v;
      const reason = e.attributionReason || (isPayrollSource(e.source) ? "nomina" : "sin_regla");
      attribution[reason] = round2((attribution[reason] || 0) + v);
      const vk = normalizeText(e.vendor);
      const vv = vendors.get(vk) || { vendor: e.vendor, total: 0, count: 0 };
      vv.total += v;
      vv.count += 1;
      vendors.set(vk, vv);
    }
    cats.set(e.category, c);
  }
  for (const m of months) {
    m.income = round2(m.income);
    m.companySpend = round2(m.companySpend);
    m.personalSpend = round2(m.personalSpend);
    m.personalIncome = round2(m.personalIncome);
    m.refunds = round2(m.refunds);
    m.net = round2(m.income - m.companySpend);
  }
  const sum = (k: "income" | "companySpend" | "personalSpend" | "personalIncome" | "refunds") => round2(months.reduce((s, m) => s + m[k], 0));
  const totals = { income: sum("income"), companySpend: sum("companySpend"), personalSpend: sum("personalSpend"), net: 0, personalIncome: sum("personalIncome"), refunds: sum("refunds") };
  totals.net = round2(totals.income - totals.companySpend);
  return {
    months,
    totals,
    payers: [...payers.values()].map((p) => ({ ...p, total: round2(p.total) })).sort((a, b) => b.total - a.total),
    categories: [...cats.values()]
      .map((c) => ({ ...c, company: round2(c.company), personal: round2(c.personal) }))
      .sort((a, b) => b.company + b.personal - (a.company + a.personal)),
    attribution,
    reimbursable: { yes: round2(reimb.yes), no: round2(reimb.no), pending: round2(reimb.pending), yesCount: reimb.yesCount, noCount: reimb.noCount, pendingCount: reimb.pendingCount },
    topVendors: [...vendors.values()].map((v) => ({ ...v, total: round2(v.total) })).sort((a, b) => b.total - a.total).slice(0, 15),
    withoutRate,
  };
}

// --- Nómina y Equipo como gasto (#613) ----------------------------------------
export interface PayslipLike {
  id: string;
  periodId?: string | null;
  periodName?: string | null;
  teamMemberId?: string | null;
  teamMemberName?: string | null;
  status?: string | null;
  currency?: string | null;
  calculation?: { netPay?: number | null; totalEmployerContribution?: number | null } | null;
  updatedAt?: string | null;
}

export interface PayrollPeriodLike {
  id: string;
  payDate?: string | null;
  endDate?: string | null;
  name?: string | null;
}

export interface SettlementLike {
  id: string;
  date?: string | null;
  status?: string | null;
  teamMemberId?: string | null;
  teamMemberName?: string | null;
  amount?: number | null;
  total?: number | null;
  payment?: number | null;
  currency?: string | null;
  description?: string | null;
}

/**
 * Fecha y monto de un recibo PAGADO: neto + aportaciones patronales, con la
 * fecha de pago del periodo (o su fin, o la última edición). `null` si el
 * recibo no está pagado o su cálculo está incompleto: no se inventa en 0.
 * Regla única para Gastos (`payrollExpenses`) y la nómina pagada (#1028).
 */
export function paidPayslipAmount(s: PayslipLike, periodById: ReadonlyMap<string, PayrollPeriodLike>): { date: string; amount: number } | null {
  if (s.status !== "paid") return null;
  const net = s.calculation?.netPay;
  const period = s.periodId ? periodById.get(s.periodId) : undefined;
  const date = [period?.payDate, period?.endDate, s.updatedAt?.slice(0, 10)].find((d) => isIsoDate(d)) as string | undefined;
  if (net == null || !Number.isFinite(net) || !date) return null;
  const employer = s.calculation?.totalEmployerContribution;
  return { date, amount: round2(net + (employer != null && Number.isFinite(employer) ? employer : 0)) };
}

/** Monto de una liquidación PAGADA (`amount` → `total` → `payment`); `null` si no hay. */
export function paidSettlementAmount(w: SettlementLike): number | null {
  if (w.status !== "paid" || !isIsoDate(w.date)) return null;
  const value = [w.amount, w.total, w.payment].find((v) => v != null && Number.isFinite(v));
  return value == null ? null : round2(value as number);
}

/**
 * Convierte recibos de nómina PAGADOS y liquidaciones PAGADAS en filas de gasto
 * de solo lectura (categoría `nomina`). Un recibo sin `netPay` (cálculo
 * incompleto) no se inventa en 0: se omite y se cuenta en `skipped`.
 */
export function payrollExpenses(input: {
  payslips: readonly PayslipLike[];
  periods: readonly PayrollPeriodLike[];
  settlements?: readonly SettlementLike[];
}): { expenses: Expense[]; skipped: number; paidKeys: string[] } {
  const periodById = new Map(input.periods.map((p) => [p.id, p]));
  const out: Expense[] = [];
  const paidKeys = new Set<string>();
  let skipped = 0;
  for (const s of input.payslips) {
    if (s.status !== "paid") continue;
    const paid = paidPayslipAmount(s, periodById);
    if (!paid) {
      skipped += 1;
      continue;
    }
    const { date, amount } = paid;
    const period = s.periodId ? periodById.get(s.periodId) : undefined;
    if (s.teamMemberId) paidKeys.add(payrollKey(s.teamMemberId, periodOf(date)));
    out.push(
      normalizeReadOnly(`payslip_${s.id}`, {
        date,
        amount,
        currency: s.currency || BASE_CURRENCY,
        vendor: s.teamMemberName || "Colaborador",
        description: `Nómina ${s.periodName || period?.name || ""}`.trim(),
        source: "nomina",
        sourceRef: { type: "payslip", id: s.id },
      }),
    );
  }
  for (const w of input.settlements || []) {
    if (w.status !== "paid") continue;
    const value = paidSettlementAmount(w);
    if (value == null) {
      skipped += 1;
      continue;
    }
    if (w.teamMemberId) paidKeys.add(payrollKey(w.teamMemberId, periodOf(w.date as string)));
    out.push(
      normalizeReadOnly(`settlement_${w.id}`, {
        date: w.date as string,
        amount: value,
        currency: w.currency || BASE_CURRENCY,
        vendor: w.teamMemberName || "Colaborador",
        description: w.description || "Liquidación de trabajo",
        source: "equipo",
        sourceRef: { type: "work_settlement", id: w.id },
      }),
    );
  }
  return { expenses: out, skipped, paidKeys: [...paidKeys] };
}

/** Fuentes que vienen de RR. HH.: no llevan factura ni se editan desde Gastos. */
export function isPayrollSource(source: unknown): boolean {
  return source === "nomina" || source === "equipo" || source === "padron";
}

/** Clave «persona × mes» con la que un pago real sustituye a la estimación. */
export function payrollKey(teamMemberId: string, period: string): string {
  return `${teamMemberId}|${period}`;
}

// --- Nómina estimada desde el padrón -------------------------------------------
/**
 * Periodos de nómina por mes según la frecuencia de pago. La empresa paga
 * quincenal (`semimonthly`), y en el padrón `fixedSalary` es lo de UNA quincena.
 */
export const PAY_PERIODS_PER_MONTH: Record<string, number> = {
  weekly: 52 / 12,
  biweekly: 26 / 12,
  semimonthly: 2,
  monthly: 1,
};
export const DEFAULT_PAY_FREQUENCY = "semimonthly";

export interface RosterMemberLike {
  id: string;
  name?: string | null;
  lastName?: string | null;
  status?: string | null;
  active?: boolean | null;
  payMode?: string | null;
  fixedSalary?: number | null;
  currency?: string | null;
  payFrequency?: string | null;
  /** Fecha de ingreso (YYYY-MM-DD): antes de ese mes no se estima nada. */
  hireDate?: string | null;
  /** Sin `hireDate`, se estima desde el mes en que entró al padrón (texto ISO o Timestamp). */
  createdAt?: unknown;
  /** Fecha de baja (YYYY-MM-DD): después de ese mes ya no se le debe nómina. */
  endDate?: string | null;
}

/**
 * Sueldo mensual de una persona según el padrón: `fixedSalary` (lo de UN
 * periodo) × periodos del mes, en su moneda. `amount: null` = sin sueldo
 * capturado (no sé no es cero). Regla única para el resumen de Gastos y la
 * nómina pagada (#1028).
 */
export function rosterMonthlySalary(m: Pick<RosterMemberLike, "fixedSalary" | "currency" | "payFrequency">): { amount: number | null; currency: string } {
  const currency = /^[A-Z]{3}$/.test(String(m.currency || "")) ? String(m.currency) : BASE_CURRENCY;
  const salary = m.fixedSalary;
  if (salary == null || !Number.isFinite(salary) || salary <= 0) return { amount: null, currency };
  const perMonth = PAY_PERIODS_PER_MONTH[m.payFrequency || DEFAULT_PAY_FREQUENCY] ?? PAY_PERIODS_PER_MONTH[DEFAULT_PAY_FREQUENCY];
  return { amount: salary * perMonth, currency };
}

/**
 * Bono individual con vigencia (#1365), tal como lo guarda Equipo en
 * `team_member_bonuses`. Espejo mínimo de `TeamMemberBonus` de @torre/team.
 */
export interface RosterBonusLike {
  id?: string;
  teamMemberId: string;
  amount: number;
  currency?: string | null;
  /** `per_period` (monto en cada periodo) o `range` (total de toda la vigencia). */
  mode?: string | null;
  startDate: string;
  endDate: string;
  active?: boolean | null;
}

const AVG_DAYS_PER_MONTH = 365.25 / 12;
const daysInclusive = (from: string, to: string): number => {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.floor((b - a) / 86400000) + 1;
};

/**
 * Lo que un bono suma a UN mes (`YYYY-MM`): los periodos de nómina del mes
 * que caen en la vigencia × lo que toca por periodo (el monto, o el total del
 * rango repartido entre sus periodos). Misma regla que `bonusMonthlyAmount`
 * en @torre/team y `modules/team/bonusMath.js`: fuera de la vigencia, 0.
 */
export function rosterBonusMonthlyAmount(bonus: RosterBonusLike, month: string, payFrequency?: string | null): number {
  if (!bonus || bonus.active === false) return 0;
  if (!isIsoDate(bonus.startDate) || !isIsoDate(bonus.endDate) || bonus.endDate < bonus.startDate) return 0;
  if (!/^\d{4}-\d{2}$/.test(String(month || ""))) return 0;
  const amount = Number(bonus.amount);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  const perMonth = PAY_PERIODS_PER_MONTH[payFrequency || DEFAULT_PAY_FREQUENCY] ?? PAY_PERIODS_PER_MONTH[DEFAULT_PAY_FREQUENCY];
  const monthStart = `${month}-01`;
  const monthEnd = lastDayOf(month);
  if (!(bonus.startDate <= monthEnd && monthStart <= bonus.endDate)) return 0;
  const perPeriod =
    (bonus.mode || "per_period") === "range"
      ? amount / Math.max(1, Math.round((daysInclusive(bonus.startDate, bonus.endDate) / AVG_DAYS_PER_MONTH) * perMonth))
      : amount;
  const from = bonus.startDate > monthStart ? bonus.startDate : monthStart;
  const to = bonus.endDate < monthEnd ? bonus.endDate : monthEnd;
  const daysInMonth = daysInclusive(monthStart, monthEnd);
  const periods = Math.max(1, Math.round((daysInclusive(from, to) / daysInMonth) * perMonth));
  return round2(perPeriod * periods);
}

export interface RosterPayrollResult {
  /** Una fila por mes y moneda: el total del equipo, no el sueldo de cada quien. */
  expenses: Expense[];
  /** Colaboradores que entraron en la estimación. */
  members: number;
  /** Con sueldo fijo pero sin monto (`null`): «no sé» no es «cero», no se suma. */
  withoutSalary: number;
  /** Monto mensual estimado por moneda en el último mes del rango. */
  monthlyByCurrency: Record<string, number>;
  /**
   * Quién entra en la estimación y con cuánto, por mes y moneda (#1424). Lleva
   * sueldos: es para el desglose que solo ven los dueños, nunca para el resumen.
   */
  detail: RosterPayrollPerson[];
  /** Quién NO se estimó y por qué: sin sueldo capturado, o ya tiene un pago registrado ese mes. */
  skipped: RosterPayrollSkipped[];
}

export interface RosterPayrollPerson {
  memberId: string;
  name: string;
  period: string;
  currency: string;
  /** Sueldo del mes: lo de un periodo × los periodos del mes. */
  salary: number;
  /** Bonos del mes en esta moneda. */
  bonus: number;
}

export interface RosterPayrollSkipped {
  memberId: string;
  name: string;
  /** `sin_sueldo`: sueldo fijo sin monto. `pagado`: ya tiene recibo, liquidación o dispersión ese mes. */
  reason: "sin_sueldo" | "pagado";
  /** Mes al que aplica (`null` si es de todo el rango). */
  period: string | null;
}

/**
 * Nómina mensual del equipo estimada desde el padrón: `fixedSalary` × periodos
 * del mes, de cada colaborador activo con sueldo fijo, desde su mes de ingreso
 * hasta `to` (sin pasar del mes en curso). Si una persona ya tiene un recibo o
 * una liquidación pagada ese mes (`paidKeys`), cuenta lo pagado y no la
 * estimación. `exchangeRates` convierte a la moneda base ({ USD: 18.4 }); sin
 * tipo de cambio la fila queda sin `amountBase`, igual que cualquier gasto.
 */
export function rosterPayrollExpenses(input: {
  members: readonly RosterMemberLike[];
  from: string;
  to: string;
  today?: string;
  paidKeys?: readonly string[];
  exchangeRates?: Record<string, number | null | undefined>;
  /**
   * Tipo de cambio de CADA mes (#1255): el vigente ese mes, no el de hoy. Si se
   * da, manda sobre `exchangeRates`; así cambiar el tipo de cambio no reescribe
   * los meses pasados.
   */
  rateFor?: (currency: string, period: string) => number | null | undefined;
  /** Bonos individuales con vigencia (#1365): se suman al mes en que se pagan. */
  bonuses?: readonly RosterBonusLike[];
}): RosterPayrollResult {
  const paid = new Set(input.paidKeys || []);
  const bonusesByMember = new Map<string, RosterBonusLike[]>();
  for (const b of input.bonuses || []) {
    if (!b || !b.teamMemberId) continue;
    const list = bonusesByMember.get(b.teamMemberId) || [];
    list.push(b);
    bonusesByMember.set(b.teamMemberId, list);
  }
  const current = input.today && isIsoDate(input.today) ? periodOf(input.today) : null;
  const last = current && current < input.to ? current : input.to;
  const months = input.from <= last ? monthRange(input.from, last) : [];
  const totals = new Map<string, { amount: number; people: number }>();
  const counted = new Set<string>();
  let withoutSalary = 0;
  const detail = new Map<string, RosterPayrollPerson>();
  const skipped: RosterPayrollSkipped[] = [];
  const nameOf = (m: RosterMemberLike) => [m.name, m.lastName].map((x) => String(x || "").trim()).filter(Boolean).join(" ") || "Colaborador";
  const person = (m: RosterMemberLike, period: string, currency: string): RosterPayrollPerson => {
    const key = `${m.id}|${period}|${currency}`;
    let row = detail.get(key);
    if (!row) {
      row = { memberId: m.id, name: nameOf(m), period, currency, salary: 0, bonus: 0 };
      detail.set(key, row);
    }
    return row;
  };
  for (const m of input.members) {
    if (m.active === false || (m.status && m.status !== "active")) continue;
    if (m.payMode !== "fixed_salary") continue;
    const monthly = rosterMonthlySalary(m);
    if (monthly.amount == null) {
      withoutSalary += 1;
      skipped.push({ memberId: m.id, name: nameOf(m), reason: "sin_sueldo", period: null });
      continue;
    }
    const currency = monthly.currency;
    const since = [m.hireDate, isoDayOf(m.createdAt)].find((d) => isIsoDate(d)) as string | undefined;
    for (const period of months) {
      if (since && period < periodOf(since)) continue;
      if (paid.has(payrollKey(m.id, period))) {
        skipped.push({ memberId: m.id, name: nameOf(m), reason: "pagado", period });
        continue;
      }
      const key = `${period}|${currency}`;
      const t = totals.get(key) || { amount: 0, people: 0 };
      t.amount += monthly.amount;
      t.people += 1;
      totals.set(key, t);
      counted.add(m.id);
      person(m, period, currency).salary += monthly.amount;
      // #1365: los bonos del mes, en su moneda (por omisión la de la persona).
      for (const b of bonusesByMember.get(m.id) || []) {
        const extra = rosterBonusMonthlyAmount(b, period, m.payFrequency);
        if (extra <= 0) continue;
        const bc = /^[A-Z]{3}$/.test(String(b.currency || "")) ? String(b.currency) : currency;
        const bk = `${period}|${bc}`;
        const bt = totals.get(bk) || { amount: 0, people: 0 };
        bt.amount += extra;
        totals.set(bk, bt);
        person(m, period, bc).bonus += extra;
      }
    }
  }
  const expenses: Expense[] = [];
  const monthlyByCurrency: Record<string, number> = {};
  for (const [key, t] of [...totals.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const [period, currency] = key.split("|");
    const rawRate = Number(input.rateFor ? input.rateFor(currency, period) : input.exchangeRates?.[currency]);
    const rate = currency === BASE_CURRENCY ? 1 : rawRate > 0 ? rawRate : null;
    const amount = round2(t.amount);
    if (period === last) monthlyByCurrency[currency] = amount;
    expenses.push(
      normalizeReadOnly(`padron_${period}_${currency}`, {
        date: lastDayOf(period),
        amount,
        currency,
        exchangeRate: rate,
        vendor: "Nómina del equipo (padrón)",
        description: `Estimado del padrón · ${t.people} ${t.people === 1 ? "colaborador" : "colaboradores"}`,
        source: "padron",
        sourceRef: { type: "team_roster", id: period },
      }),
    );
  }
  const people = [...detail.values()]
    .map((p) => ({ ...p, salary: round2(p.salary), bonus: round2(p.bonus) }))
    .sort((a, b) => a.period.localeCompare(b.period) || b.salary + b.bonus - (a.salary + a.bonus) || a.name.localeCompare(b.name));
  return { expenses, members: counted.size, withoutSalary, monthlyByCurrency, detail: people, skipped };
}

// --- Cuadre de pagos de nómina por una cuenta de dispersión (NVIO) -------------------------
/** Cuenta a la que se transfiere la nómina y a quiénes cubre. */
export interface PayoutConfig {
  /** CLABE o número de cuenta destino tal como aparece en el estado de cuenta. */
  account: string;
  /** Nombre corto para mostrar ("NVIO"). */
  label: string;
  /** Colaboradores que se pagan por esa cuenta; vacío = los que cobran en otra moneda. */
  memberIds: string[];
}

export interface PayoutTransferRow {
  id: string;
  date: string;
  /** Pesos enviados. */
  amount: number;
  /** Pesos por unidad de la moneda de la nómina ese día (`null` si no se consiguió). */
  rate: number | null;
  /** Equivalente en la moneda de la nómina (`amount / rate`). */
  foreign: number | null;
}

export interface PayoutMonth {
  period: string;
  transfers: PayoutTransferRow[];
  /** Pesos enviados en el mes. */
  sent: number;
  /** Lo enviado convertido a la moneda de la nómina con el tipo de cambio de cada día. */
  sentForeign: number | null;
  /** Nómina del mes de los colaboradores cubiertos, en su moneda (sin convertir). */
  expected: Record<string, number>;
  /** Lo esperado en la moneda de la nómina (las partes en pesos se convierten con el TC promedio del mes). */
  expectedForeign: number | null;
  /** Enviado − esperado, en la moneda de la nómina. */
  diffForeign: number | null;
  /** Enviado / esperado − 1 (0.04 = se envió 4 % de más). */
  diffPct: number | null;
  /**
   * `sin_envio`: el mes cae entre dispersiones y no tiene ninguna. Puede ser un
   * pago que no se hizo o un estado de cuenta que no se cargó: no se da por `de_menos`.
   */
  status: "cuadra" | "de_mas" | "de_menos" | "sin_tipo_de_cambio" | "sin_nomina" | "sin_envio";
}

export interface PayoutReconciliation {
  /** Moneda en la que se compara (la de los colaboradores cubiertos que no cobran en pesos, o MXN). */
  currency: string;
  members: { id: string; name: string; currency: string }[];
  months: PayoutMonth[];
  /** Totales de los meses con dispersión (los `sin_envio` van aparte en `monthsWithoutTransfer`). */
  totals: { sent: number; sentForeign: number | null; expectedForeign: number | null; diffForeign: number | null };
  monthsWithoutTransfer: string[];
  tolerance: number;
}

/** Cargo que salió hacia la cuenta de dispersión: marcado al cargarlo o con la cuenta en sus notas. */
export function isPayoutTransfer(e: Pick<Expense, "notes" | "description"> & { payoutAccount?: string | null }, account: string): boolean {
  const acc = String(account || "").replace(/\s+/g, "");
  if (!acc) return false;
  if (e.payoutAccount && String(e.payoutAccount).replace(/\s+/g, "") === acc) return true;
  return String(e.notes || "").includes(acc) || String(e.description || "").includes(acc);
}

/**
 * Cuadra lo transferido a la cuenta de dispersión contra la nómina del padrón de
 * los colaboradores que cubre. Cada transferencia se convierte con el tipo de
 * cambio de SU día (`rates["USD|2026-02-13"]`); lo esperado sale del sueldo del
 * padrón (quincena × periodos del mes). Cuadra si la diferencia cabe en
 * `tolerance` (por defecto 3 %: el diferencial cambiario de la dispersión).
 */
export function payoutReconciliation(input: {
  transfers: readonly { id: string; date: string; amount: number }[];
  members: readonly RosterMemberLike[];
  memberIds?: readonly string[];
  rates: Record<string, number | null | undefined>;
  tolerance?: number;
}): PayoutReconciliation {
  const tolerance = input.tolerance ?? 0.03;
  const salaried = input.members.filter((m) => m.payMode === "fixed_salary");
  // Elegidos a mano: cuentan aunque hoy estén inactivos (se cuadran meses pasados).
  // Sin elegir: quienes hoy cobran en otra moneda.
  const chosen =
    input.memberIds && input.memberIds.length
      ? salaried.filter((m) => input.memberIds!.includes(m.id))
      : salaried.filter((m) => m.active !== false && (!m.status || m.status === "active") && (m.currency || BASE_CURRENCY) !== BASE_CURRENCY);
  const foreignCur = chosen.map((m) => String(m.currency || BASE_CURRENCY).toUpperCase()).find((c) => c !== BASE_CURRENCY) || BASE_CURRENCY;
  const byMonth = new Map<string, PayoutTransferRow[]>();
  for (const t of [...input.transfers].sort((a, b) => a.date.localeCompare(b.date))) {
    const rate = foreignCur === BASE_CURRENCY ? 1 : Number(input.rates[`${foreignCur}|${t.date}`]) > 0 ? Number(input.rates[`${foreignCur}|${t.date}`]) : null;
    const row: PayoutTransferRow = { id: t.id, date: t.date, amount: round2(t.amount), rate, foreign: rate ? round2(t.amount / rate) : null };
    const p = periodOf(t.date);
    if (!byMonth.has(p)) byMonth.set(p, []);
    byMonth.get(p)!.push(row);
  }
  const periods = [...byMonth.keys()].sort();
  const range = periods.length ? monthRange(periods[0], periods[periods.length - 1]) : [];
  const months: PayoutMonth[] = range.map((period) => {
    const transfers = byMonth.get(period) || [];
    const expected: Record<string, number> = {};
    for (const m of chosen) {
      const salary = Number(m.fixedSalary);
      if (!(salary > 0)) continue;
      // Sin `hireDate` no se recorta: el alta en el padrón no dice cuándo empezó a cobrar.
      if (m.hireDate && isIsoDate(m.hireDate) && period < periodOf(m.hireDate)) continue;
      if (m.endDate && isIsoDate(m.endDate) && period > periodOf(m.endDate)) continue;
      const cur = String(m.currency || BASE_CURRENCY).toUpperCase();
      const perMonth = PAY_PERIODS_PER_MONTH[m.payFrequency || DEFAULT_PAY_FREQUENCY] ?? 2;
      expected[cur] = round2((expected[cur] || 0) + salary * perMonth);
    }
    const sent = round2(transfers.reduce((s, t) => s + t.amount, 0));
    const withRate = transfers.filter((t) => t.rate);
    const avgRate = withRate.length ? withRate.reduce((s, t) => s + t.amount, 0) / withRate.reduce((s, t) => s + (t.foreign || 0), 0) : null;
    const sentForeign = withRate.length === transfers.length ? round2(transfers.reduce((s, t) => s + (t.foreign || 0), 0)) : null;
    let expectedForeign: number | null = 0;
    for (const [cur, v] of Object.entries(expected)) {
      if (cur === foreignCur) expectedForeign = (expectedForeign ?? 0) + v;
      else if (cur === BASE_CURRENCY && avgRate) expectedForeign = (expectedForeign ?? 0) + v / avgRate;
      else expectedForeign = null;
      if (expectedForeign === null) break;
    }
    expectedForeign = expectedForeign === null ? null : round2(expectedForeign);
    const diffForeign = sentForeign != null && expectedForeign != null ? round2(sentForeign - expectedForeign) : null;
    const diffPct = diffForeign != null && expectedForeign ? Math.round((sentForeign! / expectedForeign - 1) * 10000) / 10000 : null;
    let status: PayoutMonth["status"];
    if (!transfers.length) status = "sin_envio";
    else if (expectedForeign === null || sentForeign == null) status = "sin_tipo_de_cambio";
    else if (expectedForeign === 0) status = "sin_nomina";
    else if (Math.abs(diffPct!) <= tolerance) status = "cuadra";
    else status = diffPct! > 0 ? "de_mas" : "de_menos";
    const empty = !transfers.length;
    return { period, transfers, sent, sentForeign: empty ? 0 : sentForeign, expected, expectedForeign, diffForeign: empty ? null : diffForeign, diffPct: empty ? null : diffPct, status };
  });
  const paid = months.filter((m) => m.status !== "sin_envio");
  const sum = (k: "sent" | "sentForeign" | "expectedForeign") => (paid.some((m) => m[k] == null) ? null : round2(paid.reduce((s, m) => s + (m[k] as number), 0)));
  const totals = { sent: sum("sent") as number, sentForeign: sum("sentForeign"), expectedForeign: sum("expectedForeign"), diffForeign: null as number | null };
  totals.diffForeign = totals.sentForeign != null && totals.expectedForeign != null ? round2(totals.sentForeign - totals.expectedForeign) : null;
  return {
    currency: foreignCur,
    members: chosen.map((m) => ({ id: m.id, name: String(m.name || m.id), currency: String(m.currency || BASE_CURRENCY).toUpperCase() })),
    months,
    totals,
    monthsWithoutTransfer: months.filter((m) => m.status === "sin_envio").map((m) => m.period),
    tolerance,
  };
}

/**
 * Tipo de cambio más reciente que la empresa ya registró, por moneda, tomado de
 * sus propios gastos (lo que el banco cobró de verdad).
 */
export function latestExchangeRates(
  expenses: readonly Pick<Expense, "currency" | "exchangeRate" | "date">[],
): Record<string, { rate: number; date: string }> {
  const out: Record<string, { rate: number; date: string }> = {};
  for (const e of expenses) {
    const c = String(e.currency || "").toUpperCase();
    if (!c || c === BASE_CURRENCY || !(Number(e.exchangeRate) > 0) || !isIsoDate(e.date)) continue;
    if (!out[c] || e.date > out[c].date) out[c] = { rate: Number(e.exchangeRate), date: e.date };
  }
  return out;
}

export function lastDayOf(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

function normalizeReadOnly(id: string, input: ExpenseInput): Expense {
  return {
    id,
    ...normalizeExpense({
      ...input,
      category: "nomina",
      paymentMethod: "transferencia",
      invoiceStatus: "no_aplica",
      status: "confirmado",
      attachments: [],
    }),
  };
}

// --- Contrato HTTP ------------------------------------------------------------
export const EXPENSES_ENDPOINTS = {
  access: "/expensesAccess",
  accessList: "/expensesAccessList",
  list: "/expensesList",
  item: "/expenses",
  attachment: "/expensesAttachment",
  attachmentUrl: "/expensesAttachmentUrl",
  extract: "/expensesExtract",
  importConfirm: "/expensesImport",
  importList: "/expensesImportList",
  importItem: "/expensesImportItem",
  summary: "/expensesSummary",
  /** Pérdidas: nómina sin horas justificadas + gastos marcados (#1177). */
  losses: "/expensesLosses",
  payrollRates: "/expensesPayrollRates",
  cashflow: "/expensesCashflow",
  /** Nómina del periodo persona por persona (#1424). Solo dueños. */
  payrollBreakdown: "/expensesPayrollBreakdown",
  /** Ingreso esperado desde el CRM (#1290): contratos fijos, por cobrar y pipeline. */
  expectedIncome: "/expensesExpectedIncome",
  /** Estado de resultados, balance general y flujo de efectivo. */
  financials: "/expensesFinancials",
  /** PUT: partidas del balance capturadas a mano. */
  balanceItems: "/expensesBalanceItems",
  payout: "/expensesPayout",
  incomeList: "/expensesIncomeList",
  income: "/expensesIncome",
  incomeExtract: "/expensesIncomeExtract",
  budgetList: "/expensesBudgetList",
  budget: "/expensesBudget",
  /** GET: vista previa del ajuste semanal + última corrida · POST: ajustar ahora (#1178). */
  budgetAdjust: "/expensesBudgetAdjust",
  /** Configuración de Finanzas internas (#1255): categorías y tipo de cambio. */
  financeCategoryList: "/expensesCategoryList",
  financeCategory: "/expensesCategory",
  financeFx: "/expensesFx",
  financeFxRate: "/expensesFxRate",
  platformList: "/expensesPlatformList",
  platform: "/expensesPlatform",
  platformReport: "/expensesPlatformReport",
  inboxStatus: "/expensesInboxStatus",
  inboxList: "/expensesInboxList",
  inboxRun: "/expensesInboxRun",
  inboxUpload: "/expensesInboxUpload",
  payrollHistory: "/expensesPayrollHistory",
  statementList: "/expensesStatementList",
  statementUpload: "/expensesStatementUpload",
  statementCode: "/expensesStatementCode",
  statementUnlock: "/expensesStatementUnlock",
  statementAccess: "/expensesStatementAccess",
  statementFile: "/expensesStatementFile",
  /** GET: compras de Activos por integrar · POST: integrarlas ahora (#1638, PR 4). */
  assetSync: "/expensesAssetSync",
} as const;

// --- Pago de plataformas (#956 / #957) ----------------------------------------
export * from "./platformBilling";
export * from "./platformInvoices";
export * from "./platformDiligence";
export * from "./platformFilters";
export * from "./platformSpend";
export * from "./platformKind";
export * from "./categorySpend";
export * from "./losses";
export * from "./spendProjection";
export * from "./budgetPlan";
export * from "./financeSettings";
export * from "./cashflowAnalysis";
export * from "./incomeExtraction";
export * from "./financialStatements";

// --- Nómina pagada contra estados de cuenta (#1028) -----------------------------
export * from "./payrollHistory";

// --- Estados de cuenta del banco (#952 – #955, épica #959) ----------------------
export * from "./accounts";
export * from "./statements";
export * from "./reconcile";
export * from "./timeline";
